import React, { useState, useEffect, useCallback } from 'react';
import { Joyride, STATUS, EVENTS } from 'react-joyride';

export const TOUR_STORAGE_KEY = 'algovex_builder_tour_seen';

const joyrideStyles = {
  tooltip: {
    borderRadius: 10,
    border: '1px solid var(--border)',
    fontSize: 13,
  },
  tooltipTitle: {
    fontSize: 15,
  },
  buttonPrimary: {
    color: 'var(--bg)',
    fontWeight: 600,
    borderRadius: 6,
  },
  buttonBack: {
    color: 'var(--muted)',
  },
  buttonSkip: {
    color: 'var(--muted)',
  },
};

const joyrideOptions = {
  skipBeacon: true,
  showProgress: true,
  buttons: ['back', 'skip', 'primary', 'close'],
  closeButtonAction: 'skip',
  arrowColor: 'var(--card)',
  backgroundColor: 'var(--card)',
  overlayColor: 'rgba(0,0,0,0.72)',
  primaryColor: 'var(--teal)',
  textColor: 'var(--text)',
  zIndex: 3000,
};

function buildSteps({ showAiStep }) {
  const steps = [
    {
      target: '[data-tour="strategy-name"]',
      title: 'Name your strategy',
      content: 'Start by giving your strategy a name — you can change it any time before saving.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="sidebar-blocks"]',
      title: 'Building blocks',
      content: 'Drag a block onto the canvas (or tap it on mobile) to add it. Indicators, price, and value blocks feed into Conditions; Conditions and Logic gates feed into Actions.',
      placement: 'right',
    },
    {
      target: '[data-tour="canvas"]',
      title: 'Wire it up',
      content: "Connect blocks by dragging from one block's edge to another. A working strategy needs at least one path into a Buy action and one into a Sell/Close action.",
      placement: 'center',
    },
  ];

  if (showAiStep) {
    steps.push({
      target: '[data-tour="ai-generate"]',
      title: 'Or let AI build it',
      content: 'Prefer plain English? Describe your strategy here and AI will assemble the blocks for you — you can still edit the result by hand.',
      placement: 'bottom',
    });
  }

  steps.push({
    target: '[data-tour="save-button"]',
    title: 'Save & backtest',
    content: 'Once your buy and sell logic are wired up, save the strategy — then you can run a backtest against historical data.',
    placement: 'bottom',
  });

  return steps;
}

// First-time guided tour for the strategy builder. Runs automatically once
// per browser (tracked via localStorage) and can be replayed via `runSignal`
// (bump the number to force a restart, e.g. from a "Help" button).
export default function BuilderTour({ showAiStep, runSignal }) {
  const [run, setRun] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem(TOUR_STORAGE_KEY)) {
      setRun(true);
    }
  }, []);

  useEffect(() => {
    if (runSignal) setRun(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runSignal]);

  const handleEvent = useCallback((data) => {
    // Mark as seen as soon as the tour actually starts, not just on completion —
    // otherwise anyone who navigates away or closes the tab mid-tour gets it
    // replayed from step 1 every time they come back.
    if (data.type === EVENTS.TOUR_START) {
      localStorage.setItem(TOUR_STORAGE_KEY, '1');
    }
    if (data.type === EVENTS.TOUR_END && (data.status === STATUS.FINISHED || data.status === STATUS.SKIPPED)) {
      setRun(false);
    }
  }, []);

  return (
    <Joyride
      run={run}
      steps={buildSteps({ showAiStep })}
      continuous
      options={joyrideOptions}
      styles={joyrideStyles}
      locale={{ last: 'Done' }}
      onEvent={handleEvent}
    />
  );
}
